import { Link } from '@tanstack/react-router';
import type { Ref } from 'react';

import { Indicator } from '#/components/ui/Indicator';
import { Ticker } from '#/components/ui/Ticker';
import { cn } from '#/utils/cn';
import { formatCurrency, roundDecimal } from '#/utils/format';

import { Icon } from '../ui/icons';

export interface TickerPriceSummaryProps {
  price: number | null | undefined;
  percentChange?: number | null;
  valueChange?: number | null;
  /** Period label appended to the value change, e.g. ' 5Y'. */
  valueChangeSuffix?: string;
  /**
   * Ticker symbol rendered as a chip ahead of the price. Hidden in the
   * unavailable state alongside the price data.
   */
  symbol?: string | null;
  /** Link the symbol chip to the equity detail page. */
  linkToEquity?: boolean;
  /** Compact variant for the sticky section nav's condensed summary row. */
  condensed?: boolean;
  /** Remove the bordered container when the surrounding layout already provides chrome. */
  showBorder?: boolean;
  /** Exposed so detail pages can use the in-place box as a scroll sentinel. */
  ref?: Ref<HTMLDivElement>;
}

/**
 * Current price plus change indicators, shared by every detail page's summary
 * section. Each product type maps its own overview fields (equity/ETF 5Y,
 * mutual fund 1Y) into the same presentation, both for the in-place summary
 * box and the condensed row that docks into the sticky section nav.
 */
export function TickerPriceSummary({
  price,
  percentChange,
  valueChange,
  valueChangeSuffix = '',
  symbol,
  linkToEquity = false,
  condensed = false,
  showBorder = true,
  ref,
}: TickerPriceSummaryProps) {
  const hasPriceSummary =
    price != null || percentChange != null || valueChange != null;

  const tickerChip = symbol ? (
    <Ticker symbol={symbol} className="block" size={condensed ? 'sm' : 'md'} />
  ) : null;

  return (
    <div
      ref={ref}
      className={cn(
        'flex items-center gap-8 rounded sm:shrink-0',
        showBorder && 'border-divider-subtle border px-16 py-8',
      )}
    >
      {hasPriceSummary ? (
        <>
          {symbol && linkToEquity ? (
            // Ticker link assumes equities, used by the NewsSection
            <Link
              to="/$category/$id"
              params={{ category: 'equities', id: symbol }}
              className="mr-12 block no-underline"
            >
              {tickerChip}
            </Link>
          ) : (
            tickerChip
          )}
          {price != null && (
            <span
              data-testid="ticker-price"
              className={cn(
                'font-semibold',
                condensed ? 'text-static-14' : 'text-static-20',
              )}
            >
              {formatCurrency(price)}
            </span>
          )}
          {percentChange != null && (
            <Indicator
              className={cn(condensed ? 'text-static-12' : 'text-static-14')}
              value={roundDecimal(percentChange) ?? 0}
              suffix="%"
              type="percent"
              label="Percent change"
            />
          )}
          {valueChange != null && (
            <Indicator
              className={cn(condensed ? 'text-static-12' : 'text-static-14')}
              value={roundDecimal(valueChange) ?? 0}
              suffix={valueChangeSuffix}
              type="value"
              label="Price change"
            />
          )}
        </>
      ) : (
        <span className="text-static-14 text-fg-utility inline-flex items-center gap-4">
          <Icon id="exclamation" size="14" className="text-error" />
          Price data unavailable
        </span>
      )}
    </div>
  );
}
